import React, {useCallback, useEffect, useRef} from "react";
import {Link, useNavigate} from "react-router-dom";
import moment from "moment";
import Box from "@mui/material/Box";
import {useAuth} from "../../../../hooks/useAuth";
import useDataTable from "../../../../hooks/useDataTable";
import CustomDataTable from "../../../../shared/tables/CustomDataTable";
import GenericButton from "../../../../shared/ui/GenericButton";
import {APIGetCustomItemsArchived} from "../api/customItems";
import {APIRestoreCustomItem} from "../api/customItem";
import {ACTIVE_ITEMS_PRINCIPAL, ACTIVE_ITEMS_TEACHER, IItem} from "./ItemsActive";

const ItemsArchived = () => {
    const {update, user} = useAuth();
    const navigate = useNavigate();

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        tableColumns,
        setTableColumns,
        totalRows,
        setTotalRows,
        perPage,
        setPerPage,
        page,
        setPage,
        query,
        submittedQuery,
        orderBy,
        order,
        handleChangePage,
        handleRequestSort,
        handleSearch,
        handleSearchChange,
        handleClearSearch,
    } = useDataTable("", "created_at", undefined, "");

    const isTeacher = user.position === "teacher";

    const pageRef = useRef(page);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const getItems = (currentPage = pageRef.current) => {
        APIGetCustomItemsArchived({page: `?page=${currentPage + 1}`, sort: orderBy, order: order, search: submittedQuery})
            .then((response) => {
                if (response.data.success) {
                    const pageData = response.data.data;

                    if(pageData.data.length === 0 && currentPage > 0) {
                        const PreviousPage = currentPage - 1;

                        setPage(PreviousPage);
                        getItems();
                        return;
                    }
                    // console.log("getTeachers OK", response);
                    setTableData(response.data.data.data.map((item: any) => mapCustomItems(item)));
                    setTotalRows(response.data.data.total);
                    setPerPage(response.data.data.per_page);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch(() => {
                // console.log("getTeachers ERR", err);
                update("error", {show: true, code: "A001"});
            })
            .finally(() => setLoading(false));
    }

    const mapCustomItems = useCallback((item: IItem) => {

        const actions = (
            <Box display="flex" justifyContent="end">
                <Box mr={1}>
                    <GenericButton
                        type="edit-small"
                        onClick={() => {
                            editItemHandler(item.id);
                        }}
                    />
                </Box>
                <Box>
                    <GenericButton type="restore" onClick={() => confirmRestore(item)}/>
                </Box>
            </Box>
        );

        return {
            id: item.id,
            title: <Link to={`/itemi-proprii/${item.id}`}>{item.title}</Link>,
            subject: item.subject,
            grade: item.grade,
            subcategory: item.subcategory_name,
            created_at: moment(item.created_at).locale("ro").format("DD.MM.YYYY"),
            ...(isTeacher && {actions: actions})
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        getItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, order, orderBy, submittedQuery]);

    useEffect(() => {
        const columns = isTeacher ? ACTIVE_ITEMS_TEACHER : ACTIVE_ITEMS_PRINCIPAL;
        setTableColumns(columns);
    }, [user.position]);

    const editItemHandler = (id: number) => {
        navigate(`/itemi-proprii/${id}/editare`);
    };

    const restoreItem = (id: string, callback: Function) => {
        update("loading", {show: true, modal: true});

        APIRestoreCustomItem({id})
            .then((response) => {
                if (response.data.success) getItems(pageRef.current);
                    else update("error", {show: true, code: "A002"});

                callback();
            })
            .catch(() => update("error", {show: true, code: "A001"}))
            .finally(() => update("loading", {show: false}));
    };

    const confirmRestore = (item: IItem) => {
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat box-archive",
            title: "Ești sigur că vrei să dezarhivezi acest item?",
            message: (
                <>
                    Itemul <b>{item.title}</b> va fi dezarhivat. Veți regăsi acest item în
                    secțiunea <b>Itemi activi.</b>
                </>
            ),
            returnFunction: (callback: Function) => {
                restoreItem(item.id.toString(), callback);
            },
            buttonYes: "Dezarhivează",
            buttonNo: "Închide",
        });
    };

    return (
        <CustomDataTable
            loading={loading}
            columns={tableColumns}
            rows={tableData}
            rowsPerPage={perPage}
            page={page}
            totalRows={totalRows}
            onPageChange={handleChangePage}
            handleRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
            hasSearch={true}
            onSearch={handleSearch}
            onChange={handleSearchChange}
            searchValue={query}
            onClearSearch={handleClearSearch}
        />
    )
}

export default ItemsArchived;